import { useState, useEffect } from 'react';
import { DAGObject, ArticleContent, getArticlePreview } from '@amorphous/core';
import { MarkdownContent } from './MarkdownContent';
import './ArticleCard.css';

interface ArticleCardProps {
  article: DAGObject<ArticleContent>;
  showHash?: boolean;
  showSignature?: boolean;
  showCreator?: boolean;
  showTimestamp?: boolean;
  cleanMode?: boolean;
  defaultCollapsed?: boolean;
  onVote?: (hash: string, value: 1 | -1) => void;
  getVoteCounts?: (hash: string) => Promise<{ up: number; down: number; total: number }>;
  getDisplayName?: (pubkey: string) => string;
  onAuthorClick?: (pubkey: string) => void;
  onShare?: (hash: string) => void;
  onReply?: (article: DAGObject<ArticleContent>) => void;
  getParentTitle?: (hash: string) => string | undefined;
  /** Whether this is the current user's own post */
  isOwnPost?: boolean;
  /** Callback to delete the article (only available for own posts) */
  onDelete?: (hash: string) => void;
}

export function ArticleCard({
  article,
  showHash = false,
  showSignature = false,
  showCreator = true,
  showTimestamp = true,
  cleanMode = false,
  defaultCollapsed = false,
  onVote,
  getVoteCounts,
  getDisplayName,
  onAuthorClick,
  onShare,
  onReply,
  getParentTitle,
  isOwnPost = false,
  onDelete,
}: ArticleCardProps) {
  const [votes, setVotes] = useState({ up: 0, down: 0, total: 0 });
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [expanded, setExpanded] = useState(false);

  // Sync collapsed state with defaultCollapsed prop
  useEffect(() => {
    setCollapsed(defaultCollapsed);
  }, [defaultCollapsed]);

  useEffect(() => {
    if (getVoteCounts) {
      getVoteCounts(article.hash).then(setVotes);
    }
  }, [article.hash, getVoteCounts]);

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    if (cleanMode) {
      return date.toLocaleDateString();
    }
    return date.toISOString();
  };

  const formatCreator = (creator: string | undefined) => {
    if (!creator) return 'Unknown';
    if (getDisplayName) {
      return getDisplayName(creator);
    }
    if (cleanMode) {
      return `${creator.slice(0, 8)}...`;
    }
    return creator;
  };

  const preview = getArticlePreview(article, cleanMode ? 300 : 200);
  const title = article.content?.title || 'Untitled';
  const body = article.content?.body || '';
  const tags = article.content?.tags || [];
  const hasMore = body.length > preview.length;
  const parentHash = article.parents?.[0];
  const parentTitle = parentHash && getParentTitle ? getParentTitle(parentHash) : undefined;

  // Collapsed view - compact single line
  if (collapsed) {
    return (
      <div
        className={`headline ${parentHash ? 'headline-reply' : ''}`}
        onClick={() => setCollapsed(false)}
      >
        <span className="headline-title">{title}</span>
        <span className="headline-meta">
          {formatCreator(article.creator)} · {formatTime(article.timestamp)}
          {onVote && <span className="headline-votes">{votes.total >= 0 ? '+' : ''}{votes.total}</span>}
        </span>
      </div>
    );
  }

  return (
    <article className={`article-card ${cleanMode ? 'clean-mode' : 'dev-mode'} ${parentHash ? 'is-reply' : ''}`}>
      {/* Header */}
      <header className="article-header">
        {parentHash && (
          <div className="reply-indicator">
            ↳ Reply{parentTitle ? ` to "${parentTitle.slice(0, 40)}${parentTitle.length > 40 ? '...' : ''}"` : ` to ${parentHash.slice(0, 8)}...`}
          </div>
        )}
        <h2 className="article-title">{title}</h2>
        {defaultCollapsed && (
          <button className="expand-collapse-btn collapse" onClick={() => setCollapsed(true)} title="Collapse">▲</button>
        )}
        {tags.length > 0 && (
          <div className="article-tags">
            {tags.map((tag: string) => (
              <span key={tag} className="tag">#{tag}</span>
            ))}
          </div>
        )}
      </header>

      {/* Body */}
      <div className="article-body">
        <MarkdownContent
          content={expanded ? body : preview}
          maxLength={expanded ? 0 : (cleanMode ? 300 : 200)}
        />
        {hasMore && (
          <button className="expand-btn" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>

      {/* Metadata */}
      <footer className="article-footer">
        <div className="article-meta">
          {showCreator && (
            <span
              className={`meta-item creator ${onAuthorClick && article.creator ? 'clickable' : ''}`}
              title={article.creator || 'Unknown'}
              onClick={onAuthorClick && article.creator ? () => onAuthorClick(article.creator) : undefined}
            >
              {formatCreator(article.creator)}
            </span>
          )}
          {showTimestamp && (
            <span className="meta-item timestamp">
              {formatTime(article.timestamp)}
            </span>
          )}
        </div>

        <div className="article-actions">
          {onReply && (
            <button
              className="action-btn reply-btn"
              onClick={() => onReply(article)}
              title="Reply to this article"
            >
              Reply
            </button>
          )}
          {onShare && (
            <button
              className="action-btn share-btn"
              onClick={() => onShare(article.hash)}
              title="Copy link"
            >
              Share
            </button>
          )}
          {isOwnPost && onDelete && (
            <button
              className="action-btn delete-btn"
              onClick={() => {
                if (confirm('Are you sure you want to delete this post? This cannot be undone.')) {
                  onDelete(article.hash);
                }
              }}
              title="Delete this post"
            >
              Delete
            </button>
          )}
          {/* Votes */}
          {onVote && (
            <div className="article-votes">
              <button
                className="vote-btn upvote"
                onClick={() => onVote(article.hash, 1)}
                title="Upvote"
              >
                + {votes.up}
              </button>
              <button
                className="vote-btn downvote"
                onClick={() => onVote(article.hash, -1)}
                title="Downvote"
              >
                - {votes.down}
              </button>
              <span className="vote-total">{votes.total}</span>
            </div>
          )}
        </div>
      </footer>

      {/* Dev mode: Show hash and signature */}
      {showHash && (
        <div className="article-debug">
          <div className="debug-row">
            <span className="debug-label">Hash:</span>
            <code className="debug-value mono">{article.hash}</code>
          </div>
          {showSignature && article.signature && (
            <div className="debug-row">
              <span className="debug-label">Sig:</span>
              <code className="debug-value mono">
                {article.signature.slice(0, 32)}...
              </code>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
